import { deleteToken, getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging';

import { getFirebaseWebApp, getFirebaseWebVapidKey, isFirebaseWebConfigured } from 'config/firebaseWeb';

export function isWebPushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && window.isSecureContext === true;
}

/** Revokes this browser’s FCM token locally (server should clear `webFcmToken` on logout). */
export async function clearWebFcmTokenOnClient(): Promise<void> {
  if (!isWebPushSupported() || !isFirebaseWebConfigured()) {
    return;
  }
  try {
    const messaging = getMessaging(getFirebaseWebApp());
    await deleteToken(messaging);
  } catch (e) {
    console.error('[webPush] clearWebFcmTokenOnClient failed', e);
  }
}

export async function obtainWebFcmToken(): Promise<string | null> {
  if (!isWebPushSupported()) {
    return null;
  }

  if (!isFirebaseWebConfigured()) {
    return null;
  }
  const vapidKey = getFirebaseWebVapidKey();
  if (!vapidKey) {
    return null;
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[webPush] Notification permission denied');
      return null;
    }

    const app = getFirebaseWebApp();
    const messaging = getMessaging(app);

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });

    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });

    return token || null;
  } catch (e) {
    console.error('[webPush] obtainWebFcmToken failed', e);
    return null;
  }
}

export function parseForegroundPayload(payload: Record<string, unknown>): { title: string; body: string } {
  const notification = payload.notification as { title?: string; body?: string } | undefined;
  const data = payload.data as Record<string, string> | undefined;
  const title = (data?.title ?? notification?.title ?? 'Notification') as string;
  const body = (data?.body ?? notification?.body ?? '') as string;
  return { title, body };
}

export function showForegroundNativeNotification(title: string, body: string): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return;
  }
  try {
    new Notification(title, { body });
  } catch (error) {
    // ignore
    console.log('Err-->', error);
  }
}

/** Returns an unsubscribe function. */
export function subscribeForegroundPush(messaging: Messaging, onIncoming: (title: string, body: string) => void): () => void {
  return onMessage(messaging, (payload) => {
    const parsed = parseForegroundPayload(payload as unknown as Record<string, unknown>);

    onIncoming(parsed.title, parsed.body);
  });
}
